import React, { createContext, forwardRef, Fragment, ReactNode, useContext, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'
import { Portal } from 'react-native-portalize'

import BottomSheetGorhom, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetScrollView,
  BottomSheetView,
} from '@gorhom/bottom-sheet'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { BOTTOM_SHEET_ANIMATION_TIMEOUT } from '@lyrahealth-inc/shared-app-logic'

import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { IS_CYPRESS_MOBILE, IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { tID } from '../../utils'

export type BottomSheetProps = {
  children: ReactNode
  snapPoints?: (string | number)[]
  onCloseEnd?: () => void
  showCloseIcon?: boolean
  /** On some context, portalized bottom sheet does not work on web. turn off if so */
  withPortal?: boolean
  /** Height of the bottom sheet is based on the content height. `snapPoints` are ignored. */
  useDynamicHeight?: boolean
  /** Use to attach the bottom sheet to the document.body on web */
  useWebPortal?: boolean
  /** Show panel handle. Hidden by default */
  showHandle?: boolean
  /** Index of snapPoints where the bottom sheet should initially be positioned. Defaults to 0 (open) */
  index?: number
  /** If content scrolls, we have to use `BottomSheetScrollView` which handles scrolling and bottom sheet gestures */
  enableScroll?: boolean
  /** set to true if bottom sheet should stay open regardless of scroll down, backdrop press, etc. */
  disableClose?: boolean
  /** Use to show content that overflows the bottom sheet (example: AppPromoBottomSheet) */
  overflowVisible?: boolean
  backgroundColor?: string
  removeBorderRadius?: boolean
  /** Set to false prevent bounce when users scrolls the modal */
  enableContentPanningGesture?: boolean
  borderRadius?: number
  /** Keyboard behavior (forwarded to @gorhom/bottom-sheet). Defaults to library default */
  keyboardBehavior?: 'extend' | 'fillParent' | 'interactive'
  keyboardBlurBehavior?: 'none' | 'restore'
  /** Android-only keyboard input mode (forwarded) */
  androidKeyboardInputMode?: 'adjustPan' | 'adjustResize'
  topInset?: number
  /** This is used if a custom view is needed instead of the default BottomSheetScrollview or BottomSheetView */
  renderChildrenWithoutContainer?: boolean
  /** If false, the bottom sheet container will not be accessible and the contents will be focusable */
  accessible?: boolean
}

const CloseIconContainer = styled(PressableOpacity)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing['24px'],
  right: theme.spacing['24px'],
  zIndex: 1,
}))

const ContentContainer = styled.View<{ overflowVisible: boolean; enableScroll: boolean; borderRadius?: number }>(
  ({ overflowVisible, enableScroll, borderRadius }) => ({
    overflow: overflowVisible ? 'visible' : enableScroll ? 'auto' : 'hidden',
    borderTopLeftRadius: borderRadius ? `${borderRadius}px` : '16px',
    borderTopRightRadius: borderRadius ? `${borderRadius}px` : '16px',
  }),
)

// This is needed by web so that the snap point can be set
// based on the height of the bottom sheet rather than in
// relation to the scroll position on the page.
const FixedWebContainer = styled.View({
  position: 'fixed',
  top: '0px',
  right: '0px',
  bottom: '0px',
  left: '0px',
  ...(IS_WEB && { pointerEvents: 'none' }),
})

export const BottomSheet = forwardRef(
  (
    {
      children,
      snapPoints = ['100%'],
      showHandle = false,
      index = 0,
      onCloseEnd = noop,
      showCloseIcon = false,
      withPortal = true,
      useDynamicHeight = false,
      useWebPortal = false,
      enableScroll = false,
      disableClose = false,
      overflowVisible = false,
      backgroundColor,
      removeBorderRadius = false,
      enableContentPanningGesture = true,
      borderRadius,
      keyboardBehavior,
      keyboardBlurBehavior,
      androidKeyboardInputMode,
      topInset,
      renderChildrenWithoutContainer,
      accessible,
    }: BottomSheetProps,
    ref: any,
  ) => {
    const { formatMessage } = useIntl()
    const value = useContext(AppContext)
    const Container = IS_WEB && !IS_CYPRESS_MOBILE ? FixedWebContainer : Fragment
    const { setModalStatusOpen } = useContext(AppContext)
    const ScrollContainer = enableScroll ? BottomSheetScrollView : Fragment
    const { colors } = useTheme()

    const handleStyle = showHandle
      ? {
          width: 40,
          backgroundColor: colors.dividerSecondary,
        }
      : { height: 0 }

    let snapPointsProcessed = snapPoints.filter((p) => p !== 0)
    snapPointsProcessed = snapPointsProcessed.length ? snapPointsProcessed : ['100%']

    useEffect(() => {
      // There seems to be a bug with the bottom sheet library where sometimes 'pointer-events' is set to 'none' on the bottom sheet backdrop on web. Force it to auto
      if (IS_WEB) {
        const timer = setTimeout(() => {
          const backdrop = document.querySelector('[aria-label*="Bottom sheet backdrop"]')
          if (backdrop && !disableClose) {
            ;(backdrop as HTMLElement).style.setProperty('pointer-events', 'auto', 'important')
          }
        }, 200)
        return () => clearTimeout(timer)
      }
      return
    }, [disableClose])

    const onClose = () => {
      setModalStatusOpen && setModalStatusOpen(false)
      onCloseEnd && onCloseEnd()
    }

    const closeSheet = () => {
      ref?.current?.close()

      // Sometimes the sheet can be dismissed really quickly before it fully opens and onChange does not trigger. Timeout is set to allow the animation to run.
      setTimeout(() => {
        onClose()
      }, BOTTOM_SHEET_ANIMATION_TIMEOUT)
    }

    const handleBottomSheetPositionChange = (index: number) => {
      if (index === -1) {
        onClose()
      } else {
        setModalStatusOpen && setModalStatusOpen(true)
      }
    }

    const CloseButton = () => (
      <CloseIconContainer
        testID={tID('BottomSheet-closeButton')}
        onPress={closeSheet}
        accessibilityLabel={formatMessage({
          defaultMessage: 'Close',
          description: 'Accessibility label for close button',
        })}
      >
        <CloseIcon size={14} />
      </CloseIconContainer>
    )

    const renderContent = () =>
      renderChildrenWithoutContainer ? (
        <>
          {showCloseIcon && <CloseButton />}
          {children}
        </>
      ) : (
        <ContentContainer
          as={enableScroll ? BottomSheetScrollView : BottomSheetView}
          testID={tID('BottomSheet-content')}
          overflowVisible={overflowVisible}
          enableScroll={enableScroll}
          borderRadius={borderRadius}
        >
          <ScrollContainer>
            {showCloseIcon && <CloseButton />}
            {children}
          </ScrollContainer>
        </ContentContainer>
      )

    const backdropComponent = (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        {...(IS_WEB && { 'data-bottom-sheet-backdrop': 'true' })}
        disappearsOnIndex={-1}
        appearsOnIndex={1}
        opacity={2} // opacity gets divided by 2?
        style={{ backgroundColor: colors.backgroundScrim }}
        pressBehavior={disableClose ? 'none' : 'close'}
        onPress={() => {
          if (!disableClose) {
            closeSheet()
          }
        }}
      />
    )

    const renderBottomSheet = () => (
      <Container>
        <AppContext.Provider value={value}>
          <BottomSheetParentContext.Provider value={true}>
            <BottomSheetGorhom
              ref={ref}
              snapPoints={snapPointsProcessed}
              index={index}
              backdropComponent={backdropComponent}
              handleIndicatorStyle={handleStyle}
              handleStyle={!showHandle && { padding: 0 }}
              enablePanDownToClose={!disableClose}
              enableContentPanningGesture={enableContentPanningGesture}
              onChange={handleBottomSheetPositionChange}
              enableDynamicSizing={useDynamicHeight}
              keyboardBehavior={keyboardBehavior}
              keyboardBlurBehavior={keyboardBlurBehavior}
              android_keyboardInputMode={androidKeyboardInputMode}
              topInset={topInset}
              backgroundStyle={{
                backgroundColor: backgroundColor ?? colors.backgroundPrimary,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: borderRadius ? borderRadius : removeBorderRadius ? 0 : 16,
                borderTopRightRadius: borderRadius ? borderRadius : removeBorderRadius ? 0 : 16,
              }}
              detached={overflowVisible}
              accessibilityRole='none'
              accessibilityLabel={null}
              accessible={accessible}
            >
              {renderContent()}
            </BottomSheetGorhom>
          </BottomSheetParentContext.Provider>
        </AppContext.Provider>
      </Container>
    )

    return withPortal ? (
      IS_WEB && useWebPortal ? (
        createPortal(renderBottomSheet(), document.body)
      ) : (
        <Portal>{renderBottomSheet()}</Portal>
      )
    ) : (
      renderBottomSheet()
    )
  },
)

export const BottomSheetParentContext = createContext(false)
